import { faArchive, faTrash, faTrashRestore } from "@fortawesome/pro-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { Button, Modal, Tabs, Typography } from "antd"
import { useContext, useEffect, useRef, useState } from "react"
import { AuthenticationContext } from "../../AuthenticationContext"
import { NavigationService } from "../../services/navigationService"
import { canManageSalesLeads } from "../SystemSetup/SalesLeads/salesLeadsModel"
import CustomerAttachments from "../attachments/CustomerAttachments"
import AttachmentUpload from "../attachments/AttachmentUpload"
import { Attachment } from "../attachments/attachmentsModel"
import CustomerJobs from "../jobs/CustomerJobs"
import CustomerPaymentMethods from "../invoicing/CustomerPaymentMethods/CustomerPaymentMethods"
import AddSalesLeadModal from "./AddSalesLeadModal"
import CreateNewJobModal from "./CreateNewJobModal"
import ImportQuickBooksCustomersModal from "./ImportQuickBooksCustomersModal"
import CustomerConsultsTable from "./CustomerConsultsTable"
import CustomerHero from "./CustomerHero"
import CustomerInfoForm from "./CustomerInfoForm"
import CustomerIssuedInvoices from "./CustomerIssuedInvoices"
import CustomerPhotoGalleryModal from "./CustomerPhotoGalleryModal"
import CustomerPropertiesTab from "./CustomerPropertiesTab"
import CustomerQuickBooksLink from "./CustomerQuickBooksLink"
import CustomerVisitsTable from "./CustomerVisitsTable"
import CustomersSidebar from "./CustomersSidebar"
import ScheduledJobs from "./ScheduledJobs"
import { Consult, CustomerDetail } from "./customerApi"
import { upsertConsult } from "./customerConsultsModel"
import { parseNewHashAction } from "./createNewJobModel"
import { toCustomerFormValues, toCustomerSaveRequest } from "./customerInfoFormModel"
import { decideCustomerHydration } from "./customersPageModel"
import {
  useCustomerDetailQuery,
  useDeleteCustomerMutation,
  useRestoreCustomerMutation,
  useSalesLeadsQuery,
  useSaveCustomerMutation,
} from "./customerQueries"
import "./CustomersPage.css"

const { Text } = Typography

const googleMapsApiKey = (window as unknown as { env?: { GOOGLE_API_KEY?: string } }).env?.GOOGLE_API_KEY

type Props = {
  showDeleted: boolean
  navigationService: NavigationService
}

const newBlankCustomer = (isCustomer: boolean): CustomerDetail => ({
  id: 0,
  url: '',
  displayName: 'New Customer',
  displayAs: 0,
  isActive: true,
  isCustomer,
  countryCode: '',
  state: '',
  properties: 0,
  attachments: 0,
  projects: 0,
  phones: [],
  emails: [],
})

const CustomersPage = ({ showDeleted, navigationService }: Props) => {
  const { currentUser } = useContext(AuthenticationContext)
  const canAccessInvoicing = currentUser?.roles.includes('Accounting') ?? false

  const [selectedCustomerId, setSelectedCustomerId] = useState<number | null>(null)
  const [newCustomer, setNewCustomer] = useState<CustomerDetail | null>(null)
  const [sidebarTrigger, setSidebarTrigger] = useState(0)
  const [newlyCreatedCustomerId, setNewlyCreatedCustomerId] = useState<number | undefined>()

  const [customer, setCustomer] = useState<CustomerDetail | undefined>()
  const [consults, setConsults] = useState<Consult[]>([])
  const [dirty, setDirty] = useState(false)
  const [primaryPhotoId, setPrimaryPhotoId] = useState<string | null | undefined>(undefined)
  const [primaryPhotoUrl, setPrimaryPhotoUrl] = useState<string | null | undefined>(undefined)
  const [uploadOpen, setUploadOpen] = useState(false)
  const [createNewOpen, setCreateNewOpen] = useState(false)
  const [newConsultRequested, setNewConsultRequested] = useState(false)
  const [qboOpen, setQboOpen] = useState(false)
  const [qboImportOpen, setQboImportOpen] = useState(false)
  const [photoGalleryOpen, setPhotoGalleryOpen] = useState(false)
  const [activeTab, setActiveTab] = useState('info')
  const [addSalesLeadOpen, setAddSalesLeadOpen] = useState(false)
  const selectAddedSalesLeadRef = useRef<((name: string) => void) | null>(null)

  const formValuesRef = useRef<ReturnType<typeof toCustomerFormValues> | null>(null)
  const hydratedIdRef = useRef<number | null>(null)
  const hydratedPrimaryPhotoIdRef = useRef<string | null | undefined>(undefined)

  const customerQuery = useCustomerDetailQuery(newCustomer ? null : selectedCustomerId, showDeleted)
  const salesLeadsQuery = useSalesLeadsQuery()
  const saveMutation = useSaveCustomerMutation()
  const deleteMutation = useDeleteCustomerMutation()
  const restoreMutation = useRestoreCustomerMutation()

  const photoDirty = primaryPhotoId !== hydratedPrimaryPhotoIdRef.current

  const applyHydration = (data: CustomerDetail) => {
    setCustomer(data)
    setConsults(data.consults ?? [])
    formValuesRef.current = toCustomerFormValues(data)
    setPrimaryPhotoId(data.primaryPhotoId ?? null)
    setPrimaryPhotoUrl(undefined)
    hydratedPrimaryPhotoIdRef.current = data.primaryPhotoId ?? null
    setDirty(false)
  }

  useEffect(() => {
    const decision = decideCustomerHydration({
      newCustomer,
      queryData: customerQuery.data,
      hydratedId: hydratedIdRef.current,
      dirty,
      photoDirty,
    })
    if (decision.kind === 'none') {
      return
    }
    if (decision.kind === 'full') {
      hydratedIdRef.current = decision.hydratedId
      applyHydration(decision.data)
      setActiveTab('info')
      return
    }
    setCustomer(decision.data)
    setConsults(decision.data.consults ?? [])
    formValuesRef.current = toCustomerFormValues(decision.data)
    setPrimaryPhotoId(decision.data.primaryPhotoId ?? null)
    hydratedPrimaryPhotoIdRef.current = decision.data.primaryPhotoId ?? null
  }, [customerQuery.data, newCustomer])

  const isDeletedItem = showDeleted

  const selectCustomer = (id: number) => {
    setNewCustomer(null)
    setSelectedCustomerId(id)
  }

  const addCustomer = (isCustomer = false) => {
    setSelectedCustomerId(null)
    setNewCustomer(newBlankCustomer(isCustomer))
  }

  useEffect(() => {
    const action = parseNewHashAction(navigationService.hash)
    if (action === 'customer' || action === 'lead') {
      navigationService.setHash('')
      addCustomer(action === 'customer')
    }
  }, [])

  const beforeSelect = () => {
    if (dirty || photoDirty) {
      return window.confirm('This customer has been edited.\n\nDiscard your changes and select a different customer?')
    }
    return true
  }

  const markDirty = () => setDirty(true)

  const save = () => {
    if (!customer) {
      return
    }
    const base = formValuesRef.current ?? toCustomerFormValues(customer)
    const { customer: request, createFirstPropertyAutomatically } = toCustomerSaveRequest({
      ...base,
      primaryPhotoId,
      consults,
    })
    saveMutation.mutate(
      { customer: request, createFirstPropertyAutomatically },
      {
        onSuccess: saved => {
          setNewCustomer(null)
          hydratedIdRef.current = saved.id
          applyHydration(saved)
          setSelectedCustomerId(saved.id)
          setSidebarTrigger(x => x + 1)
          setNewlyCreatedCustomerId(saved.id)
        },
      }
    )
  }

  const removeCustomer = () => {
    if (!customer) {
      return
    }
    if (customer.projects > 0) {
      Modal.error({ title: 'Cannot remove', content: 'This customer cannot be removed because there are associated jobs. To remove this customer, please first remove the jobs.' })
      return
    }
    Modal.confirm({
      title: 'Remove this customer?',
      content: 'This cannot be undone and will remove all information regarding this customer and properties.',
      onOk: () => deleteMutation.mutate(customer.id, {
        onSuccess: () => {
          setCustomer(undefined)
          setSelectedCustomerId(null)
          setSidebarTrigger(x => x + 1)
        },
      }),
    })
  }

  const restoreCustomer = () => {
    if (!customer) {
      return
    }
    Modal.confirm({
      title: 'Restore this customer?',
      onOk: () => restoreMutation.mutate(customer.id, {
        onSuccess: () => {
          setCustomer(undefined)
          setSelectedCustomerId(null)
          setSidebarTrigger(x => x + 1)
        },
      }),
    })
  }

  const toggleRemoveConsult = (consult: Consult) => {
    setConsults(prev => prev.map(c => (c.id === consult.id ? { ...c, removed: !c.removed } : c)))
    markDirty()
  }

  const selectPrimaryPhoto = (attachment: Attachment) => {
    setPrimaryPhotoId(attachment.id)
    setPrimaryPhotoUrl(attachment.url)
  }

  const tabItems = customer ? [
    { key: 'info', label: 'Info' },
    { key: 'properties', label: 'Properties' },
    { key: 'work', label: 'Work' },
    { key: 'schedule', label: 'Schedule' },
    { key: 'visits', label: 'Visits' },
    { key: 'attachments', label: 'Attachments' },
    ...(canAccessInvoicing ? [{ key: 'invoicing', label: 'Invoicing' }] : []),
  ] : []

  const tabContent: Record<string, JSX.Element> = customer ? {
    info: <CustomerInfoForm
      customer={customer}
      salesLeads={salesLeadsQuery.data ?? []}
      onAddSalesLead={canManageSalesLeads(currentUser) ? selectSalesLead => {
        selectAddedSalesLeadRef.current = selectSalesLead
        setAddSalesLeadOpen(true)
      } : undefined}
      googleMapsApiKey={googleMapsApiKey}
      disabled={isDeletedItem}
      onChange={values => { formValuesRef.current = values }}
      onDirtyChange={setDirty}
      consultsSlot={<section>
        <CustomerConsultsTable
          customer={customer}
          consults={consults}
          formDirty={dirty}
          onToggleRemoveConsult={toggleRemoveConsult}
          onConsultSaved={saved => {
            setConsults(prev => upsertConsult(prev, saved))
            customerQuery.refetch()
          }}
          newConsultRequested={newConsultRequested}
          onNewConsultOpened={() => setNewConsultRequested(false)}
        />
      </section>}
    />,
    properties: <CustomerPropertiesTab customer={customer} navigationService={navigationService} />,
    work: <section>
      <Text strong><FontAwesomeIcon icon={faArchive} /> Customer Jobs</Text>
      <div style={{ marginTop: 12 }}>
        <CustomerJobs customerId={customer.id} />
      </div>
    </section>,
    schedule: <ScheduledJobs customerId={customer.id} />,
    visits: <section>
      <CustomerVisitsTable customerId={customer.id} />
    </section>,
    attachments: <CustomerAttachments customerId={customer.id} />,
    invoicing: <>
      <CustomerPaymentMethods customerId={customer.id} />
      <CustomerIssuedInvoices customerId={customer.id} />
    </>,
  } : {}

  return <div className="customers-view customers-react-page">
    <CustomersSidebar
      onAdd={() => addCustomer(false)}
      onBeforeSelect={beforeSelect}
      onSelect={record => selectCustomer(record.id)}
      onImport={() => setQboImportOpen(true)}
      updateTrigger={sidebarTrigger}
      navigationService={navigationService}
      showDeleted={showDeleted}
      newCustomerId={newlyCreatedCustomerId}
    />

    <div id="main" className="main-customer has-elements fluid-container">
      {!customer && <div className="overlay">
        <i className="fas fa-users" />
        {!isDeletedItem && <span>SELECT A CUSTOMER</span>}
        {isDeletedItem && <div>
          <p>DELETED CUSTOMERS</p>
          <p>(click on any customer to restore)</p>
        </div>}
      </div>}

      <ImportQuickBooksCustomersModal
      open={qboImportOpen}
      onClose={() => setQboImportOpen(false)}
      onImported={() => setSidebarTrigger(x => x + 1)}
    />

    {customer && <>
        <CustomerHero
          customer={customer}
          isDeletedItem={isDeletedItem}
          dirty={dirty || photoDirty}
          isSaving={saveMutation.isLoading}
          primaryPhotoUrl={primaryPhotoUrl}
          onNew={() => setCreateNewOpen(true)}
          onWorkTab={() => setActiveTab('work')}
          onAttachmentsTab={() => setActiveTab('attachments')}
          onOpenPhotoGallery={() => setPhotoGalleryOpen(true)}
          onUpload={() => setUploadOpen(true)}
          onQbo={() => setQboOpen(true)}
          onSave={save}
        />

        <section className="customer-page__tabs">
          <Tabs centered activeKey={activeTab} onChange={setActiveTab} items={tabItems} />
        </section>

        {tabContent[activeTab]}

        {activeTab === 'info' && <div style={{ marginTop: 16 }}>
          {!isDeletedItem && customer.id > 0 && <Button danger type="primary" icon={<FontAwesomeIcon icon={faTrash} />} onClick={removeCustomer}>Remove Customer</Button>}
          {isDeletedItem && <Button type="primary" icon={<FontAwesomeIcon icon={faTrashRestore} />} onClick={restoreCustomer}>Restore Customer</Button>}
        </div>}
      </>}
    </div>

    {customer && <>
      <AddSalesLeadModal
        open={addSalesLeadOpen}
        onCancel={() => setAddSalesLeadOpen(false)}
        onAdded={name => {
          setAddSalesLeadOpen(false)
          selectAddedSalesLeadRef.current?.(name)
        }}
      />
      <CreateNewJobModal
        open={createNewOpen}
        customer={customer}
        onClose={() => setCreateNewOpen(false)}
        onCreateConsultation={() => {
          setActiveTab('info')
          setNewConsultRequested(true)
        }}
        navigate={url => { navigationService.setUrl(url.replace('#!/', '')) }}
      />
      <CustomerQuickBooksLink
        open={qboOpen}
        customer={customer}
        onClose={() => setQboOpen(false)}
        onLinked={() => customerQuery.refetch()}
      />
      <CustomerPhotoGalleryModal
        open={photoGalleryOpen}
        customerId={customer.id}
        currentPrimaryPhotoId={primaryPhotoId}
        onSelect={selectPrimaryPhoto}
        onClose={() => setPhotoGalleryOpen(false)}
      />
      <AttachmentUpload
        isOpen={uploadOpen}
        customerId={customer.id}
        defaultFolder="This Customer"
        onClose={() => setUploadOpen(false)}
      />
    </>}
  </div>
}

export default CustomersPage
